import React, { useState } from "react";
import { Card, Form, Input, Button, Typography, message, Row, Col } from "antd";
import { UserOutlined, LockOutlined, LoginOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import axiosInstance from "../../configs";

const { Title, Text } = Typography;

export default function LoginPage() {
  const [loading, setLoading] = useState(false);

  const onFinish = async (values) => {
    try {
      setLoading(true);

      const res = await axiosInstance.post("/auth/login", {
        username: values.username,
        password: values.password,
      });

      if (res.data?.success) {
        const { token, refreshToken, user, permissions } = res.data.data;

        localStorage.setItem("token", token);
        localStorage.setItem("refreshToken", refreshToken);
        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("permissions", JSON.stringify(permissions || []));

        message.success("Login berhasil");
        window.location.hash = "#/dashboard";
      } else {
        message.error(res.data?.message || "Login gagal");
      }
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || "Terjadi kesalahan saat login";
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <style>{`
        .login-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          background: linear-gradient(135deg, #0d4f4f 0%, #0f766e 30%, #14b8a6 70%, #2dd4bf 100%);
          position: relative;
          overflow: hidden;
        }
        
        .bg-circle-1 {
          position: absolute;
          top: -50%;
          left: -20%;
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(45, 212, 191, 0.15) 0%, transparent 70%);
          border-radius: 50%;
          animation: pulse 8s ease-in-out infinite;
        }
        
        .bg-circle-2 {
          position: absolute;
          bottom: -30%;
          right: -10%;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(20, 184, 166, 0.12) 0%, transparent 70%);
          border-radius: 50%;
          animation: pulse 10s ease-in-out infinite reverse;
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
        
        .login-card {
          transition: all 0.3s ease;
        }
        
        .login-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25) !important;
        }
        
        .feature-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 0;
          border-bottom: 1px solid rgba(255,255,255,0.1);
        }
        
        .feature-item:last-child {
          border-bottom: none;
        }
        
        .feature-icon {
          width: 36px;
          height: 36px;
          min-width: 36px;
          background: rgba(255,255,255,0.15);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
        }
        
        .main-title {
          font-size: 32px;
          line-height: 1.2;
          font-weight: 800;
          margin-bottom: 12px;
          color: white;
        }
        
        .branding-panel {
          color: white;
          padding: 24px 20px;
        }
        
        .branding-desc {
          font-size: 14px;
          color: rgba(255,255,255,0.85);
          max-width: 380px;
          line-height: 1.6;
          margin-bottom: 20px;
        }
        
        /* Mobile Responsive */
        @media (max-width: 991px) {
          .main-title {
            font-size: 26px;
            text-align: center;
          }
          
          .branding-panel {
            padding: 20px 16px;
            text-align: center;
          }
          
          .branding-desc {
            font-size: 13px;
            margin: 0 auto 16px;
          }
          
          .feature-list {
            display: none;
          }
          
          .badge-container {
            justify-content: center;
          }
        }
        
        @media (max-width: 576px) {
          .login-page {
            padding: 12px;
          }
          
          .main-title {
            font-size: 22px;
          }
          
          .branding-panel {
            padding: 16px 12px;
          }
        }
      `}</style>

      {/* Animated Background */}
      <div className="bg-circle-1" />
      <div className="bg-circle-2" />

      <Row
        gutter={[24, 16]}
        justify="center"
        align="middle"
        style={{ width: "100%", maxWidth: 950, zIndex: 1 }}
      >
        {/* LEFT: Branding */}
        <Col xs={{ span: 24, order: 2 }} lg={{ span: 12, order: 1 }}>
          <div className="branding-panel">
            {/* Badge */}
            <div
              className="badge-container"
              style={{
                display: "flex",
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "6px 12px",
                  borderRadius: 999,
                  backgroundColor: "rgba(255,255,255,0.15)",
                  backdropFilter: "blur(10px)",
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: 0.8,
                  textTransform: "uppercase",
                }}
              >
                <SafetyCertificateOutlined style={{ marginRight: 6, fontSize: 12 }} />
                Inventory System
              </div>
            </div>

            {/* Title */}
            <h1 className="main-title">
              Pasifik<br />
              <span style={{
                background: "linear-gradient(90deg, #99f6e4, #5eead4)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}>
                Logistik Berikat
              </span>
            </h1>

            <p className="branding-desc">
              Sistem manajemen inventaris modern untuk mengelola pemasukan,
              pengeluaran, dan pelaporan pabean.
            </p>

            {/* Features - Hidden on mobile */}
            <div className="feature-list">
              <div className="feature-item">
                <div className="feature-icon">📦</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 12 }}>Manajemen Stok Real-time</div>
                  <div style={{ fontSize: 10, opacity: 0.7 }}>Pantau inventaris langsung</div>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">📊</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 12 }}>Laporan Otomatis</div>
                  <div style={{ fontSize: 10, opacity: 0.7 }}>Generate laporan pabean</div>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">🔒</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 12 }}>Keamanan Terjamin</div>
                  <div style={{ fontSize: 10, opacity: 0.7 }}>Data terenkripsi & aman</div>
                </div>
              </div>
            </div>
          </div>
        </Col>

        {/* RIGHT: Login Card */}
        <Col xs={{ span: 24, order: 1 }} lg={{ span: 10, order: 2 }}>
          <div style={{ display: "flex", justifyContent: "center", padding: "0 8px" }}>
            <Card
              className="login-card"
              style={{
                width: "100%",
                maxWidth: 380,
                borderRadius: 18,
                background: "rgba(255, 255, 255, 0.98)",
                border: "none",
                boxShadow: "0 16px 40px rgba(0, 0, 0, 0.18)",
              }}
              bodyStyle={{ padding: "24px 22px" }}
            >
              {/* Logo - Smaller */}
              <div style={{ marginBottom: 18, textAlign: "center" }}>
                <img
                  src="/assets/media/logos/alb.png"
                  alt="Logo"
                  style={{
                    height: 60,
                    objectFit: "contain",
                    marginBottom: 12,
                  }}
                />

                <Title level={5} style={{ margin: "0 0 2px", color: "#0f766e", fontWeight: 700, fontSize: 16 }}>
                  Selamat Datang
                </Title>
                <Text style={{ color: "#64748b", fontSize: 12 }}>
                  Masuk ke dashboard
                </Text>
              </div>

              <Form layout="vertical" onFinish={onFinish} requiredMark={false}>
                <Form.Item
                  label={<span style={{ color: "#334155", fontWeight: 600, fontSize: 12 }}>Username</span>}
                  name="username"
                  rules={[{ required: true, message: "Username wajib diisi" }]}
                  style={{ marginBottom: 14 }}
                >
                  <Input
                    size="large"
                    prefix={<UserOutlined style={{ color: "#14b8a6" }} />}
                    placeholder="Masukkan username"
                    style={{
                      borderRadius: 10,
                      border: "2px solid #e2e8f0",
                    }}
                  />
                </Form.Item>

                <Form.Item
                  label={<span style={{ color: "#334155", fontWeight: 600, fontSize: 12 }}>Password</span>}
                  name="password"
                  rules={[{ required: true, message: "Password wajib diisi" }]}
                  style={{ marginBottom: 18 }}
                >
                  <Input.Password
                    size="large"
                    prefix={<LockOutlined style={{ color: "#14b8a6" }} />}
                    placeholder="Masukkan password"
                    style={{
                      borderRadius: 10,
                      border: "2px solid #e2e8f0",
                    }}
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 10 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    icon={<LoginOutlined />}
                    block
                    loading={loading}
                    style={{
                      height: 46,
                      borderRadius: 10,
                      fontWeight: 700,
                      fontSize: 14,
                      background: "linear-gradient(135deg, #0f766e, #14b8a6)",
                      border: "none",
                      boxShadow: "0 8px 20px rgba(20, 184, 166, 0.35)",
                    }}
                  >
                    Masuk
                  </Button>
                </Form.Item>
              </Form>

              <div style={{
                textAlign: "center",
                padding: "10px",
                background: "#f0fdfa",
                borderRadius: 8,
                marginTop: 6,
              }}>
                <Text style={{ color: "#0f766e", fontSize: 10 }}>
                  🔐 Jangan bagikan kredensial Anda
                </Text>
              </div>

              <div style={{ textAlign: "center", marginTop: 10 }}>
                <Text style={{ color: "#94a3b8", fontSize: 10 }}>
                  © {new Date().getFullYear()} Pasifik Logistik Berikat
                </Text>
              </div>
            </Card>
          </div>
        </Col>
      </Row>
    </div>
  );
}
